import katex from 'katex';
/* Enregistre \ce et \pu sur le singleton KaTeX : la chimie est une matiere du
   secondaire, et sans cette extension \ce{H2O} sort en « \ce » rouge suivi de
   « H2O » compose comme des maths — une formule fausse d'apparence credible. */
import 'katex/contrib/mhchem';

/*
 * RichDoc (ProseMirror/TipTap JSON) → HTML string. Pure, dependency-light
 * (only KaTeX for math), so it renders identically on the web (on-screen view)
 * and server-side (assignment PDF). Text is always escaped — the Markdown
 * editor stores html:false, so text nodes are literal and must never be
 * interpolated raw. Math nodes carry verbatim LaTeX in attrs.content.
 */

type Mark = { readonly type: string; readonly attrs?: Record<string, unknown> };
type Node = {
  readonly type: string;
  readonly attrs?: Record<string, unknown>;
  readonly content?: readonly Node[];
  readonly text?: string;
  readonly marks?: readonly Mark[];
};

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/*
 * `throwOnError: false` ne degrade pas proprement : sur une commande inconnue
 * KaTeX rend le nom de la commande en rouge PUIS compose le reste en maths.
 * `\SI{9.81}{m/s^2}` devient « \SI 9.81 m/s² » — une formule d'apparence
 * credible, que l'enseignant peut imprimer sans voir qu'elle est fausse. On
 * prefere echouer visiblement : la source LaTeX verbatim, marquee comme non
 * rendue, se corrige ; une fausse formule se diffuse.
 *
 * INVARIANT : la mention porte dans le BALISAGE, jamais dans une feuille de
 * style. Elle a d'abord vecu dans un `::after` du CSS d'impression : la meme
 * formule sortait donc signalee au PDF et muette partout ailleurs, et porter
 * la regle dans un second CSS l'aurait dupliquee au lieu de l'unifier. Le sens
 * « cette formule n'a pas ete rendue » appartient au contenu ; chaque surface
 * ne fait que l'habiller.
 */
const MATH_FALLBACK_LABEL = 'formule non rendue';

export function renderMathHtml(tex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(tex, { displayMode, throwOnError: true });
  } catch {
    const source = escapeHtml(tex);
    if (!displayMode) {
      return `<code class="math-non-rendue" title="${MATH_FALLBACK_LABEL}">${source}</code>`;
    }
    return (
      `<div class="math-non-rendue-bloc"><code class="math-non-rendue">${source}</code>` +
      `<span class="math-non-rendue-legende">${MATH_FALLBACK_LABEL}</span></div>`
    );
  }
}

/*
 * Les attributs d'une marque textStyle viennent du contenu saisi : ils sont
 * réinjectés dans un attribut style, donc filtrés par liste blanche stricte et
 * jamais interpolés tels quels. Toute valeur hors motif est ignorée.
 */
const COLOR_PATTERN = /^#[0-9a-f]{3}$|^#[0-9a-f]{6}$|^rgb\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*\)$/i;
const FONT_SIZE_PATTERN = /^\d{1,3}(?:\.\d+)?(?:px|pt|em|rem|%)$/;

function textStyleCss(attrs: Record<string, unknown> | undefined): string {
  const declarations: string[] = [];
  const color = attrs?.color;
  if (typeof color === 'string' && COLOR_PATTERN.test(color.trim())) {
    declarations.push(`color:${color.trim()}`);
  }
  const fontSize = attrs?.fontSize;
  if (typeof fontSize === 'string' && FONT_SIZE_PATTERN.test(fontSize.trim())) {
    declarations.push(`font-size:${fontSize.trim()}`);
  }
  return declarations.join(';');
}

/*
 * Meme discipline que textStyle ci-dessus : `textAlign` vient du contenu saisi
 * et repart dans un attribut style, donc liste blanche stricte et jamais
 * d'interpolation directe — ce walker alimente le chat, l'editeur ET le PDF,
 * une valeur non filtree serait une injection sur les trois surfaces.
 *
 * L'alignement part en style INLINE, jamais en classe : ce HTML est consomme
 * par plusieurs surfaces qui ont chacune leur feuille de style, et une classe
 * devrait etre redefinie dans chacune — le sens « ce bloc est centre »
 * appartient au contenu, pas a l'habillage de telle surface.
 */
const TEXT_ALIGNMENTS: ReadonlySet<string> = new Set(['left', 'center', 'right', 'justify']);

function blockAlignAttr(attrs: Record<string, unknown> | undefined): string {
  const align = attrs?.textAlign;
  if (typeof align !== 'string' || !TEXT_ALIGNMENTS.has(align)) return '';
  return ` style="text-align:${align}"`;
}

function applyMarks(html: string, marks: readonly Mark[] | undefined): string {
  if (!marks || marks.length === 0) return html;
  return marks.reduce((acc, mark) => {
    switch (mark.type) {
      case 'bold':
        return `<strong>${acc}</strong>`;
      case 'italic':
        return `<em>${acc}</em>`;
      case 'strike':
        return `<s>${acc}</s>`;
      case 'code':
        return `<code>${acc}</code>`;
      case 'textStyle': {
        const style = textStyleCss(mark.attrs);
        return style ? `<span style="${style}">${acc}</span>` : acc;
      }
      case 'link': {
        const href = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '#';
        return `<a href="${escapeHtml(href)}" rel="noreferrer">${acc}</a>`;
      }
      default:
        return acc;
    }
  }, html);
}

function renderChildren(nodes: readonly Node[] | undefined): string {
  if (!nodes) return '';
  return nodes.map(renderNode).join('');
}

function renderNode(node: Node): string {
  switch (node.type) {
    case 'doc':
      return renderChildren(node.content);
    case 'paragraph':
      return `<p${blockAlignAttr(node.attrs)}>${renderChildren(node.content)}</p>`;
    case 'heading': {
      const level = Math.min(Math.max(Number(node.attrs?.level ?? 2), 1), 6);
      const align = blockAlignAttr(node.attrs);
      return `<h${level}${align}>${renderChildren(node.content)}</h${level}>`;
    }
    case 'text':
      return applyMarks(escapeHtml(node.text ?? ''), node.marks);
    case 'hardBreak':
      return '<br>';
    case 'horizontalRule':
      return '<hr>';
    case 'bulletList':
      return `<ul>${renderChildren(node.content)}</ul>`;
    case 'orderedList':
      return `<ol>${renderChildren(node.content)}</ol>`;
    case 'listItem':
      return `<li>${renderChildren(node.content)}</li>`;
    case 'blockquote':
      return `<blockquote>${renderChildren(node.content)}</blockquote>`;
    case 'codeBlock':
      return `<pre><code>${renderChildren(node.content)}</code></pre>`;
    case 'inlineMath':
      return renderMathHtml(String(node.attrs?.content ?? ''), false);
    case 'blockMath':
      return renderMathHtml(String(node.attrs?.content ?? ''), true);
    case 'table':
      return `<table>${renderChildren(node.content)}</table>`;
    case 'tableRow':
      return `<tr>${renderChildren(node.content)}</tr>`;
    case 'tableHeader':
      return `<th>${renderChildren(node.content)}</th>`;
    case 'tableCell':
      return `<td>${renderChildren(node.content)}</td>`;
    default:
      return renderChildren(node.content);
  }
}

/** Convert a RichDoc (ProseMirror JSON) to an HTML string. Empty/invalid → ''. */
export function richDocToHtml(doc: unknown): string {
  if (!doc || typeof doc !== 'object') return '';
  return renderNode(doc as Node);
}
